// De taak in een venster over de lijst Taken (ontwerp Taakbeheer; Rens, 23/10: een modal, geen nieuwe pagina): alles
// van één taak op één scherm, zonder tabbladen. Links wat u schrijft (omschrijving, checklist, verloop, bijlagen),
// rechts wat u instelt (planning, eigenaars, labels, bucket, fase, mijlpaal, wacht op, uren, doel). Elke wijziging
// gaat meteen naar Planner, met Ongedaan maken; de secties komen uit components/task/ en zijn dezelfde als in de
// taaklade. Bovenaan staat wat nog ontbreekt om uitgewerkt te zijn. Een afgewerkte taak (filter Afgewerkt) is alleen
// te lezen, met Heropenen. Het adres blijft deelbaar: /taken?taak=<id>; het oude /taken/<id> stuurt daarnaartoe door.
import { useQueryClient } from "@tanstack/react-query";
import { Fragment, useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router";
import { api } from "../lib/api";
import { useClockifyLive } from "../lib/api-clockify";
import { catOf, labelColor } from "../lib/board";
import { missing, missingSentence, type Missing } from "../lib/complete";
import { faseChain } from "../lib/fases";
import { useIndex, useTaskOps } from "../lib/hooks";
import { Icon } from "../lib/icons";
import { ddmm, deadline, dmy, PRIORITIES, priorityLabel } from "../lib/planning";
import type { Fase, PlanningData, Task, TaskOp } from "../lib/types";
import { Av } from "../components/Avatar";
import { DateField } from "../components/DateField";
import { clockifyReady } from "../components/TaskCard";
import { TaskHistory } from "../components/TaskHistory";
import { TaskHours } from "../components/TaskHours";
import { AttachmentsSection } from "../components/task/Attachments";
import { ChecklistSection } from "../components/task/Checklist";
import { DepsSection } from "../components/task/Deps";
import { LabelsSection } from "../components/task/Labels";
import { OwnersSection } from "../components/task/Owners";
import { firstNames, useTaskPerm } from "../components/task/perm";
import { useToast } from "../shell/Toast";

const BOX: Record<Missing, string> = { schatting: "tk-uren", eigenaar: "tk-plan", datums: "tk-plan" };

/** /taken/:id (oude adressen en links van buiten): de lijst, met de taak in het venster. */
export function Taak() {
  const { id = "" } = useParams();
  return <Navigate to={"/taken?taak=" + encodeURIComponent(id)} replace />;
}

/** Het venster over de lijst: de hele taak; sluiten met Escape, het kruisje of een klik ernaast. */
export function TaskModal({ task, data, onClose }: { task: Task; data: PlanningData; onClose: () => void }) {
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => { box.current?.focus(); }, []);
  // Escape sluit het venster, ook als de focus net uit een veld viel (na Bewaren of Annuleren). In een veld laat Escape
  // het veld los (titel, punt, omschrijving) of blijft ze in de tekst van een update; de kalender van een datumveld
  // vangt haar eigen Escape al op. Alleen zolang het venster openstaat.
  useEffect(() => {
    const h = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented || /^(INPUT|TEXTAREA|SELECT)$/.test((e.target as HTMLElement).tagName)) return;
      e.preventDefault();
      onClose();
    };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onClose]);
  return (
    <>
      <button type="button" className="p-scrim" aria-label="Venster sluiten" onClick={onClose} />
      <div ref={box} className="p-tk-modal" role="dialog" aria-modal="true" aria-label={task.title} tabIndex={-1}>
        <TaskPage key={task.id} task={task} data={data} onClose={onClose} />
      </div>
    </>
  );
}

function TaskPage({ task, data, onClose }: { task: Task; data: PlanningData; onClose: () => void }) {
  const perm = useTaskPerm(task);
  const idx = useIndex(data);
  const ops = useTaskOps();
  const toast = useToast();
  const qc = useQueryClient();
  const [, setParams] = useSearchParams();
  const { data: liveData } = useClockifyLive();
  const [title, setTitle] = useState<string | null>(null);
  const [desc, setDesc] = useState<string | null>(null);
  const [faseOpen, setFaseOpen] = useState(false);
  const faseOpts = useMemo(() => {
    const own = data.fases.filter((x) => x.projectId === task.projectId && x.progress < 1), ids = new Set(own.map((x) => x.id));
    const out: { f: Fase; lvl: number }[] = [];
    const walk = (parent: string | null, lvl: number, seen: Set<string>) => {
      for (const f of own.filter((x) => (parent ? x.parentId === parent : !x.parentId || !ids.has(x.parentId))).sort((a, b) => a.order - b.order || a.title.localeCompare(b.title))) {
        if (seen.has(f.id)) continue;
        out.push({ f, lvl });
        walk(f.id, lvl + 1, new Set([...seen, f.id]));
      }
    };
    walk(null, 0, new Set());
    return out;
  }, [data.fases, task.projectId]);
  if (!perm) return null;

  const { me, med, own, canEdit, canPick, readOnly, write, inPlanner, finishOther, direct, proposeEnd, proposal, url } = perm;
  const edit = perm.edit && !task.done, plan = perm.plan && !task.done;
  const cat = catOf(task), d = deadline(task.end, data.today);
  const klant = idx.klanten.get(task.projectId)?.name || "", bucket = idx.buckets.get(task.bucketId)?.name || "";
  const chain = faseChain(task.faseId, idx.fases);
  const ownerNames = task.owners.map((o) => idx.people.get(o.personId)?.first || "iemand");
  const live = liveData && clockifyReady(liveData.clockify) ? liveData : null;
  const miss = task.done ? [] : missing(task, live?.tasks[task.id], !!live);
  const goal = task.goalId ? data.goals.find((g) => g.id === task.goalId) : null;
  const run = (o: TaskOp[], message: string) => { ops.run(task, o, message).catch(() => {}); };
  const openFase = (id: string) => setParams((p) => { p.delete("taak"); p.set("fase", id); return p; });

  // datums: meteen opgeslagen; een start na de deadline neemt de deadline mee, een deadline voor de start de start
  const pickStart = (s: string) => { if (s !== task.start) run([{ kind: "dates", start: s, ...(s > task.end ? { end: s } : {}) }], "Start " + ddmm(s) + (s > task.end ? ", deadline mee" : "") + ": " + task.title); };
  const pickEnd = async (e: string) => {
    if (e === task.end) return;
    if (direct) { run([{ kind: "dates", end: e, ...(e < task.start ? { start: e } : {}) }], "Deadline " + ddmm(e) + ": " + task.title); return; }
    try {
      await api.propose(task.id, e);
      qc.invalidateQueries({ queryKey: ["proposals"] });
      toast.flash("Voorstel " + ddmm(e) + " verstuurd naar de planner");
    } catch (x) { toast.flash("Voorstel niet verstuurd: " + (x instanceof Error ? x.message : "er ging iets mis"), { error: true }); }
  };
  const saveTitle = () => { const v = (title || "").trim(); setTitle(null); if (v && v !== task.title) run([{ kind: "title", text: v }], "Titel gewijzigd: " + v); };
  const saveDesc = () => { const v = (desc || "").trim(); setDesc(null); if (v !== task.notes) run([{ kind: "notes", text: v }], "Omschrijving opgeslagen · zichtbaar in Planner binnen 1 à 2 minuten"); };
  const onKey = (e: KeyboardEvent, ok: () => void, cancel: () => void) => { if (e.key === "Enter") { e.preventDefault(); ok(); } if (e.key === "Escape") { e.preventDefault(); cancel(); } };
  const goTo = (k: Missing) => {
    const el = document.getElementById(BOX[k]);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("p-tk-flash");
    window.setTimeout(() => el.classList.remove("p-tk-flash"), 1600);
  };
  const finish = () => { run([{ kind: "progress", progress: 1 }], "Afgewerkt: " + task.title); onClose(); };
  const moveFase = (v: string) => {
    setFaseOpen(false);
    const f = v ? idx.fases.get(v) : null;
    if ((f?.id || null) === (task.faseId || null)) return;
    run([{ kind: "parent", faseId: f ? f.id : null }], f ? task.title + " staat nu onder " + f.title : task.title + " staat niet meer onder een fase");
  };

  return (
    <div className="p-tk-page">
      <div className="p-tk-dh">
        <div style={{ minWidth: 0, flex: 1 }}>
          <p className="ex-eyebrow" style={{ margin: 0 }}>{klant} · {bucket}</p>
          {chain.length > 0 && (
            <div className="p-dw-pad"><Icon name="fase" size={13} />
              {chain.map((f, i) => <Fragment key={f.id}>{i > 0 && <span>›</span>}<button type="button" className="p-dw-padl" title="Fase openen" onClick={() => openFase(f.id)}>{f.title}</button></Fragment>)}
            </div>
          )}
          <div className="p-tk-title">
            {title != null
              ? <><input className="ex-input" value={title} maxLength={250} autoFocus aria-label="Titel" onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => onKey(e, saveTitle, () => setTitle(null))} />
                <button type="button" className="ex-btn ex-btn--primary" onClick={saveTitle}>Bewaren</button><button type="button" className="ex-btn" onClick={() => setTitle(null)}>Annuleren</button></>
              : <><h1>{task.title}</h1>{edit && <button type="button" className="p-tk-pen" title="Titel wijzigen" aria-label="Titel wijzigen" onClick={() => setTitle(task.title)}><Icon name="pen" size={16} /></button>}</>}
          </div>
          <div className="p-tk-pills">
            {task.done
              ? <span className="ex-pill ex-pill--ok"><span className="ex-dot" />Afgewerkt · gewijzigd {dmy(task.modified)}</span>
              : <span className={"ex-pill ex-pill--" + (cat === "actief" ? d.tone || "off" : "off")}><span className="ex-dot" />{cat === "actief" ? d.long : cat === "geparkeerd" ? "Geparkeerd" : "Klaar om af te sluiten"}</span>}
            {task.labels.map((l) => <span key={l.id} className="p-lbl"><i style={{ background: labelColor(l.name) }} />{l.name}</span>)}
            {task.critical && !task.done && <span className="p-crit">Kritisch pad</span>}
            {task.milestone && <span className="p-meta-i" style={{ fontSize: 13 }}><Icon name="diamond" size={12} />Mijlpaal</span>}
            {task.priority <= 3 && <span className={"p-prio p" + task.priority}>{priorityLabel(task.priority)}</span>}
            {task.pending && <span className="p-meta-i t-mute">Planner verwerkt een wijziging</span>}
          </div>
        </div>
        <div className="p-tk-acts">
          {task.done && canEdit && write && <button type="button" className="ex-btn" onClick={() => run([{ kind: "progress", progress: 0 }], "Heropend: " + task.title)}>Heropenen</button>}
          {!task.done && <Link className="ex-btn" to={"/planning?taak=" + task.id} title="De taak op het bord, in de lade"><Icon name="board" size={15} />Op het bord</Link>}
          <a className="ex-btn" href={url} target="_blank" rel="noreferrer" title="Deze taak openen in Planner"><Icon name="ext" size={15} />Planner</a>
          {(edit || (finishOther && !task.done)) && <button type="button" className="ex-btn" style={{ color: "var(--t-ok)", borderColor: "var(--ok-bd)", background: "var(--ok-bg)" }} onClick={finish}><Icon name="check" size={15} />Afwerken</button>}
          {canPick && write && !task.done && <button type="button" className="ex-btn ex-btn--primary" onClick={() => run([{ kind: "owner", personId: me.id, on: true }], "U bent nu eigenaar van " + task.title)}>Zelf oppakken</button>}
          {inPlanner && !task.done && <a className="ex-btn ex-btn--primary" href={url} target="_blank" rel="noreferrer"><Icon name="ext" size={15} />{canPick ? "Oppakken in Planner" : "Openen in Planner"}</a>}
          <button type="button" className="ex-icon-btn ex-icon-btn--square" aria-label="Sluiten" title="Sluiten (Esc)" onClick={onClose}><Icon name="x" size={18} /></button>
        </div>
      </div>

      {readOnly && !task.done && (
        <div className="ex-notice ex-notice--mist p-notice"><Icon name="info" size={16} />
          <p>{task.owners.length ? "Taak van " + firstNames(ownerNames) + ". U kunt ze bekijken; wijzigen en afwerken doet " + firstNames(ownerNames) + " of de planner."
            : write ? "Deze taak heeft nog geen eigenaar. U kunt ze zelf oppakken; aan een collega toewijzen doet de planner."
            : "Deze taak heeft nog geen eigenaar. Oppakken doet u in Planner: wijs de taak daar aan uzelf toe."}</p>
        </div>
      )}
      {inPlanner && canEdit && !task.done && (
        <div className="ex-notice ex-notice--mist p-notice"><Icon name="info" size={16} />
          <p>Wijzigen en afwerken doet u in Planner. Wat u daar aanpast, staat binnen een minuut ook hier.{med ? " Een nieuwe deadline stelt u hier voor aan de planner." : ""} De update en de timer werken wel vanuit de app.</p>
        </div>
      )}
      {proposal && (
        <div className="ex-notice ex-notice--info p-notice"><Icon name="info" size={16} />
          <p>{proposal.by === me.id ? "Uw voorstel" : "Een voorstel"} voor een nieuwe deadline, {dmy(proposal.end)}, wacht op de planner.</p>
        </div>
      )}
      {edit && miss.length > 0 && (
        <div className="ex-notice ex-notice--warning p-notice"><Icon name="alert" size={16} />
          <p>Nog uit te werken: {miss.map((k, i) => <Fragment key={k}>{i > 0 && (i === miss.length - 1 ? " en " : ", ")}<button type="button" className="ex-link" style={{ fontSize: 14 }} onClick={() => goTo(k)}>{missingSentence([k])}</button></Fragment>)}.</p>
        </div>
      )}

      <div className="p-tk-grid">
        <div className="p-tk-col">
          <section className="p-tk-box" id="tk-desc">
            <div className="p-tk-boxh"><h3>Omschrijving</h3>
              {edit && desc == null ? <button type="button" className="ex-link p-icl" onClick={() => setDesc(task.notes)}><Icon name="pen" size={13} />{task.notes ? "Wijzigen" : "Schrijven"}</button> : <span className="p-kd-note" style={{ margin: 0 }}>de notitie in Planner</span>}
            </div>
            {desc != null
              ? <>
                <textarea className="ex-input p-tk-ta" value={desc} autoFocus aria-label="Omschrijving" placeholder="Wat moet er gebeuren, voor wie, en wat moet een collega weten om het over te nemen?" onChange={(e) => setDesc(e.target.value)} onKeyDown={(e) => { if (e.key === "Escape") setDesc(null); }} />
                <div className="p-quick" style={{ alignItems: "center" }}><button type="button" className="ex-btn ex-btn--primary" onClick={saveDesc}>Opslaan</button><button type="button" className="ex-btn" onClick={() => setDesc(null)}>Annuleren</button><span className="p-kd-note" style={{ margin: 0 }}>Gaat naar Planner, als tekst met regeleinden</span></div>
              </>
              : <p className={"p-tk-desc" + (task.notes ? "" : " empty")}>{task.notes || "Nog geen omschrijving."}</p>}
          </section>

          {(task.checklist.length > 0 || edit) && <section className="p-tk-box" id="tk-check"><ChecklistSection task={task} perm={{ ...perm, edit }} run={run} page /></section>}

          <section className="p-tk-box" id="tk-verloop">
            <div className="p-tk-boxh"><h3>Verloop</h3><span className="p-kd-note" style={{ margin: 0 }}>updates en wijzigingen, het nieuwste onderaan</span></div>
            <div className="p-tk-vl"><TaskHistory task={task} data={data} canLabel={edit} write={write} owners={firstNames(ownerNames)} plannerUrl={url} /></div>
          </section>

          <section className="p-tk-box" id="tk-att"><AttachmentsSection task={task} perm={perm} run={run} page /></section>
        </div>

        <div className="p-tk-col">
          <section className="p-tk-box" id="tk-plan">
            <h3>Planning</h3>
            <div className="p-tk-kv">
              <span className="k">Start</span><span className="v"><DateField label="Start (vanaf wanneer)" value={task.start} today={data.today} other={task.end} otherLabel="Deadline" onPick={direct && !task.done ? pickStart : undefined} /></span>
              <span className="k">Deadline</span><span className="v"><DateField label={proposeEnd ? "Deadline (voorstel aan de planner)" : "Deadline"} value={task.end} today={data.today} other={task.start} otherLabel="Start" align="right" onPick={(direct || proposeEnd) && !task.done ? pickEnd : undefined} />{proposeEnd && !task.done && <span className="p-kd-note" style={{ margin: 0 }}>als voorstel aan de planner</span>}</span>
              <span className="k">Prioriteit</span><span className="v">{edit
                ? <span className="p-quick">{PRIORITIES.map(([p, label]) => <button key={p} type="button" className={"p-tog p-tog-s" + (task.priority === p ? " on" : "")} onClick={() => run([{ kind: "priority", priority: p }], "Prioriteit " + label.toLowerCase() + ": " + task.title)}>{label}</button>)}</span>
                : <span className={"p-prio p" + task.priority}>{priorityLabel(task.priority)}</span>}</span>
              <span className="k">Eigenaars</span><span className="v" style={{ display: "block" }}><OwnersSection task={task} data={data} perm={{ ...perm, plan }} run={run} /></span>
              <span className="k">Labels</span><span className="v"><LabelsSection task={task} data={data} perm={{ ...perm, edit }} run={run} always /></span>
              {/* ook onder een fase: Planner laat een subtaak in een andere bucket dan haar fase staan (live getest 26/10) */}
              <span className="k">Bucket</span><span className="v">{plan
                ? <select className="ex-input p-tk-sel" aria-label="Bucket" value={task.bucketId} onChange={(e) => run([{ kind: "bucket", bucketId: e.target.value }], "Bucket " + (idx.buckets.get(e.target.value)?.name || "") + ": " + task.title)}>
                  {data.buckets.filter((b) => b.projectId === task.projectId).sort((a, b) => a.name.localeCompare(b.name)).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
                : <b>{bucket}</b>}</span>
              <span className="k">Fase</span><span className="v">{faseOpen
                ? <><select className="ex-input p-tk-sel" aria-label="Fase" autoFocus value={task.faseId || ""} style={{ maxWidth: "100%" }} onChange={(e) => moveFase(e.target.value)}>
                    <option value="">Geen fase</option>
                    {faseOpts.map(({ f, lvl }) => <option key={f.id} value={f.id}>{"— ".repeat(Math.min(lvl, 4))}{f.title}</option>)}
                  </select><button type="button" className="ex-btn-xs" onClick={() => setFaseOpen(false)}>Annuleren</button></>
                : <>{chain.length
                  ? <span className="p-dw-pad" style={{ margin: 0 }}><Icon name="fase" size={13} />{chain.map((f, i) => <Fragment key={f.id}>{i > 0 && <span>›</span>}<button type="button" className="p-dw-padl" title="Fase openen" onClick={() => openFase(f.id)}>{f.title}</button></Fragment>)}</span>
                  : <span className="p-kd-note" style={{ margin: 0 }}>geen fase</span>}
                  {plan && <button type="button" className="ex-link p-icl" onClick={() => setFaseOpen(true)}>{chain.length ? "Verplaatsen" : "Onder een fase zetten"}</button>}</>}</span>
              <span className="k">Mijlpaal</span><span className="v">{plan
                ? <button type="button" className={"p-tog" + (task.milestone ? " on" : "")} aria-pressed={task.milestone} onClick={() => run([{ kind: "milestone", on: !task.milestone }], task.milestone ? "Geen mijlpaal meer: " + task.title : "Mijlpaal: " + task.title)}><Icon name="diamond" size={12} />{task.milestone ? "Mijlpaal" : "Geen mijlpaal"}</button>
                : <span>{task.milestone ? "Ja" : "Nee"}</span>}</span>
            </div>
          </section>

          <section className="p-tk-box" id="tk-deps"><DepsSection task={task} data={data} perm={{ ...perm, plan }} ops={ops} page /></section>

          <section className="p-tk-box" id="tk-uren">
            <h3>Uren · Clockify</h3>
            {task.done
              ? <p className="p-kd-note" style={{ margin: 0 }}>De uren van een afgewerkte taak staan in Clockify; heropen de taak om ze hier te zien.</p>
              : <div className="p-tk-ur">
                <TaskHours task={task} me={me} klant={klant} today={data.today} manage={!med || own} planner={!med} owners={firstNames(ownerNames)} firstOf={(pid) => idx.people.get(pid)?.first || "iemand"} />
              </div>}
          </section>

          {goal && (
            <section className="p-tk-box"><div className="p-tk-boxh"><h3>Doel</h3><span className="p-kd-note" style={{ margin: 0 }}>uit Planner</span></div>
              <span className="p-tk-goal"><Icon name="flag" size={14} /><b>{goal.name}</b><small>· {goal.done} van {goal.total} taken af</small></span>
            </section>
          )}
          {task.owners.length > 0 && task.done && (
            <section className="p-tk-box"><h3>Eigenaars</h3><div className="p-owners" style={{ marginTop: 0 }}>{task.owners.map((o) => { const p = idx.people.get(o.personId); return <span key={o.personId} className="p-tog on p-owner"><Av id={o.personId} init={p?.initials || "?"} cls="p-av s" />{p?.name || "Onbekend"}</span>; })}</div></section>
          )}
        </div>
      </div>
    </div>
  );
}
